/**
 * useBreathCycle — owns the breath-phase state machine + countdown timer.
 *
 *  - Phases cycle: inhale → hold → exhale → inhale …
 *  - Each phase counts down 1 Hz; total session time also counts down.
 *  - Calling `start()` resets and begins; `stop()` ends; component cleanup
 *    on unmount cancels the interval automatically.
 *
 *  Returns the values + handlers the UI needs to render the breath circle:
 *      { phase, countdown, totalTime, isActive, start, stop }
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const BREATH_PATTERN = [4, 4, 6]; // inhale, hold, exhale (14-second cycle)
const SESSION_SECONDS = 60;

export { BREATH_PATTERN };

export const useBreathCycle = (onComplete) => {
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState('inhale');
  const [countdown, setCountdown] = useState(BREATH_PATTERN[0]);
  const [totalTime, setTotalTime] = useState(SESSION_SECONDS);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const start = useCallback(() => {
    setIsActive(true);
    setPhase('inhale');
    setCountdown(BREATH_PATTERN[0]);
    setTotalTime(SESSION_SECONDS);
  }, []);

  const stop = useCallback(() => {
    setIsActive(false);
    setPhase('inhale');
    setCountdown(BREATH_PATTERN[0]);
    onCompleteRef.current?.();
  }, []);

  useEffect(() => {
    if (!isActive) return undefined;
    const timer = setInterval(() => {
      setTotalTime((prev) => {
        if (prev <= 1) {
          stop();
          return 0;
        }
        return prev - 1;
      });
      setCountdown((prevCountdown) => {
        if (prevCountdown > 1) return prevCountdown - 1;
        // Phase transition needed - compute next phase based on CURRENT phase state
        const nextPhase = getNextPhase(phase);
        setPhase(nextPhase);
        // Return the duration for the NEW phase we just transitioned to
        return nextDurationFor(nextPhase);
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isActive, phase, stop]);

  return { phase, countdown, totalTime, isActive, start, stop };
};

function getNextPhase(currentPhase) {
  if (currentPhase === 'inhale') {
    return BREATH_PATTERN[1] > 0 ? 'hold' : 'exhale';
  }
  if (currentPhase === 'hold') return 'exhale';
  return 'inhale';
}

function nextDurationFor(newPhase) {
  // Return the duration for the phase we're transitioning TO
  if (newPhase === 'hold') return BREATH_PATTERN[1]; // hold duration
  if (newPhase === 'exhale') return BREATH_PATTERN[2]; // exhale duration
  return BREATH_PATTERN[0]; // inhale duration
}
